import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { homeData } from "../../data/homeData";
import { assetPath } from "../../utils/assetPath";
import GradientLine from "../common/GradientLine";

/* ── Gradient stops for the continuous blue gradient flowing through all rows ── */
const gradientStops = [
  { from: "#397AD9", to: "#123C79" }, // RAPTOR  bright blue
  { from: "#123C79", to: "#4439D9" }, // LearnOn  medium blue
  { from: "#4439D9", to: "#00003D" }, // Aurora  deep blue
  { from: "#00003D", to: "#185AA8" }, // GRIFFIN  darker blue
  { from: "#185AA8", to: "#1F1C5C" }, // CourseAI  darkest blue
];

/* ── Animation variants ── */
const textVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const img1Variants = {
  hidden: { opacity: 0, x: -40, scale: 0.92 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] },
  },
};

const img2Variants = {
  hidden: { opacity: 0, x: 40, scale: 0.92 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ── Individual Product Row ── */
const ProductRow = ({ product, index }) => {
  const isEven = index % 2 === 0;
  const gradient = gradientStops[index] || gradientStops[0];

  return (
    <div
      className="relative w-full text-white overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
      }}
    >
      {/* Subtle radial glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: isEven
            ? "radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.08), transparent 60%)"
            : "radial-gradient(ellipse at 70% 50%, rgba(255,255,255,0.08), transparent 60%)",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16 py-20 md:py-28 relative z-10">
        <div
          className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-16 ${
            isEven ? "lg:flex-row-reverse" : ""
          }`}
        >
          {/* Text Side */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-120px" }}
            className={`w-full lg:w-[40%] flex flex-col justify-center z-10 will-change-[transform,opacity] ${
              isEven ? "items-start text-left" : "items-start text-left"
            }`}
          >
            <span className="text-white/50 font-bold tracking-widest uppercase text-xs mb-3">
              {product.tag}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight leading-[1.1]">
              {product.title}
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed font-medium mb-10 max-w-lg">
              {product.description}
            </p>

            <Link
              to={`/product/${product.id}`}
              className="group inline-flex items-center gap-2 text-white font-bold tracking-wide text-sm
                         border border-white/20 rounded-sm px-7 py-3
                         hover:bg-white hover:text-blue-900
                         transition-all duration-300
                         shadow-[0_0_20px_rgba(255,255,255,0.05)]
                         hover:shadow-[0_0_30px_rgba(255,255,255,0.15)]"
            >
              {product.cta}
              <span className="text-lg leading-none transition-transform group-hover:translate-x-1">
                →
              </span>
            </Link>
          </motion.div>

          {/* Image Side  Staggered dual images */}
          <div className="w-full lg:w-[60%] relative flex items-center justify-center min-h-[300px] md:min-h-[500px]">
            {/* Image 1  Primary (larger, back) */}
            <motion.div
              variants={isEven ? img2Variants : img1Variants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className={`absolute ${
                isEven
                  ? "left-0 top-0 w-[55%] md:w-[40%]"
                  : "right-0 top-0 w-[55%] md:w-[40%]"
              } h-[200px] sm:h-[300px] md:h-[400px] z-10 will-change-transform`}
            >
              <div className="w-full h-full overflow-hidden group cursor-pointer">
                <img
                  src={assetPath(product.image1)}
                  alt={`${product.title} - View 1`}
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </motion.div>

            {/* Image 2  Secondary (smaller, front, overlapping) */}
            <motion.div
              variants={isEven ? img1Variants : img2Variants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className={`absolute ${
                isEven
                  ? "right-0 bottom-0 w-[70%] md:w-[55%]"
                  : "left-0 bottom-0 w-[70%] md:w-[55%]"
              } h-[150px] sm:h-[200px] md:h-[300px] z-20 will-change-transform`}
            >
              <div className="w-full h-full overflow-hidden group cursor-pointer">
                <img
                  src={assetPath(product.image2)}
                  alt={`${product.title} - View 2`}
                  decoding="async"
                  className={`w-full h-full ${product.id === "learnon" ? "object-contain object-left-bottom" : "object-cover"} transition-transform duration-700 ease-out group-hover:scale-105`}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main Section ── */
const ProductEngines = () => {
  const { products } = homeData;

  return (
    <section
      id="platforms"
      className="relative pt-24 bg-[#ECF0F4] overflow-hidden"
    >
      {/* Header section  two-column: label left, content right */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-16 relative z-10 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-16 will-change-[transform,opacity]"
        >
          {/* Left  Label */}
          <span className="flex items-center gap-2 text-slate-700 text-sm font-bold tracking-wide whitespace-nowrap shrink-0">
            ✦ Our Products
          </span>

          {/* Right  Heading + Description */}
          <div className="flex flex-col gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-[4.5rem] leading-[1.1] font-semibold text-slate-800 tracking-tight text-balance">
              {products.heading}
            </h2>
            <p className="text-slate-500 text-base md:text-lg leading-relaxed max-w-2xl font-medium">
              From competency management to adaptive learning, immersive
              simulation, predictive analytics, and intelligent content every
              product is integrated into a single training intelligence layer.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Product rows with continuous gradient */}
      <div className="flex flex-col w-full m-0 p-0">
        {/* Top transition and line */}
        <div
          style={{
            background: `linear-gradient(180deg, #ECF0F4 0%, ${gradientStops[0].from} 100%)`,
          }}
          className="py-0"
        ></div>

        {products.items.map((product, index) => (
          <React.Fragment key={product.id}>
            <ProductRow product={product} index={index} />
            {index < products.items.length - 1 && (
              <div
                style={{
                  background: `linear-gradient(180deg, ${gradientStops[index]?.to || "#123C79"} 0%, ${gradientStops[index + 1]?.from || "#172554"} 100%)`,
                }}
                className="py-2"
              >
                <GradientLine color="white" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default ProductEngines;
